import styles from "./Designer.module.css";
import { sanitizeColorName } from "../../utils/designerUtils";

const PALETTE_PREVIEW_LIMIT = 6;

const PaletteChevronIcon = ({ expanded = false }) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        {expanded ? (
            <path d="M7 14.5L12 9.5L17 14.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
            <path d="M7 9.5L12 14.5L17 9.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        )}
    </svg>
);

const SectionTitle = ({ title, subtitle }) => (
    <div className={styles.wizardSectionHeader}>
        <h4 className={styles.wizardSectionTitle}>{title}</h4>
        {subtitle ? <p className={styles.wizardSectionSubtitle}>{subtitle}</p> : null}
    </div>
);

const MaterialColorView = ({
    activePartNode,
    selectedKey = false,
    selectedMaterialId,
    selectedColor,
    materialOptions = [],
    onFetchColors,
    onTogglePalette,
    paletteOpen = false,
    paletteLoading = false,
    availableColors = [],
    onSelectColor,
    canSublimate = false,
    isSublimated = false,
    onToggleSublimation,
    fullprints = [],
    selectedFullprint = null,
    onSelectFullprint,
}) => {
    if (!activePartNode) {
        return (
            <section className={styles.sectionCard}>
                <div className={styles.emptyState}>Seleccioná una parte en el paso anterior.</div>
            </section>
        );
    }

    const partLabel = activePartNode?.Item?.nname || activePartNode?.Item?.name || `Parte ${activePartNode?.key}`;
    const hasMaterials = materialOptions.length > 0;

    return (
        <section className={styles.sectionCard}>
            <SectionTitle
                title={`Color de ${partLabel}`}
                subtitle="Elegí un color compatible para esta parte."
            />

            {!selectedKey ? (
                <div className={styles.emptyState}>Activá esta parte para poder configurarla.</div>
            ) : (
                <div className={styles.materialWizardGrid}>
                    {!hasMaterials ? (
                        <div className={styles.noPrintsMessage}>Esta parte no tiene materiales configurables.</div>
                    ) : null}

                    {canSublimate && hasMaterials && selectedMaterialId ? (
                        <div className={styles.sublimationRow}>
                            <span className={styles.sublimationLabel}>Sublimación</span>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    checked={isSublimated}
                                    onChange={(e) => onToggleSublimation(activePartNode.key, e.target.checked)}
                                />
                                <span className={styles.slider}></span>
                            </label>
                            <span className={styles.sublimationHint}>
                                {isSublimated ? "Modo fullprint activo" : "Modo color estándar"}
                            </span>
                        </div>
                    ) : null}

                    {isSublimated ? (
                        <div className={styles.gridGap10}>
                            <div className={styles.inputLabel}>Elegí fullprint</div>
                            <div className={styles.gridFullprints}>
                                {fullprints.map((fp) => {
                                    const selected = selectedFullprint?.id === fp.id;
                                    const icon = fp.icon || fp.icon_url || fp.iconUrl;
                                    return (
                                        <button
                                            key={`fp-${fp.id}`}
                                            type="button"
                                            onClick={() => onSelectFullprint(activePartNode.key, fp)}
                                            className={`${styles.fullprintCard} ${selected ? styles.fullprintCardActive : ""}`}
                                        >
                                            <div className={styles.fullprintCardBody}>
                                                {icon ? <img src={icon} alt={fp.name} className={styles.fullprintCardImg} /> : null}
                                                <div className={styles.fullprintName}>{fp.name || `Fullprint ${fp.id}`}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    className={styles.noFullprintBtn}
                                    onClick={() => onSelectFullprint(activePartNode.key, null)}
                                >
                                    Sin fullprint
                                </button>
                            </div>
                        </div>
                    ) : selectedMaterialId ? (
                        <div className={styles.gridGap10}>
                            <div className={styles.flexWrap}>
                                <span className={styles.inputLabel}>Color</span>
                            </div>
                            <div
                                className={`${styles.colorSwatchWrap} ${styles.paletteSwipeContainer} ${
                                    paletteOpen ? styles.paletteSwipeExpanded : styles.paletteSwipeCollapsed
                                }`}
                            >
                                {availableColors.map((color) => (
                                    <button
                                        key={`color-${color.id}`}
                                        type="button"
                                        className={`${styles.colorSwatch} ${
                                            (selectedColor?.id ?? selectedColor?.color_id) === color.id ? styles.colorSwatchSelected : ""
                                        }`}
                                        style={{
                                            background: color.code || "linear-gradient(45deg, #eaeaea, #cdcdcd)",
                                        }}
                                        onClick={() => onSelectColor(activePartNode.key, selectedMaterialId, color)}
                                        title={`${sanitizeColorName(color.name, "Color")} (${color.code || "sin código"})`}
                                    />
                                ))}
                                {!paletteOpen && availableColors.length > PALETTE_PREVIEW_LIMIT ? (
                                    <span className={styles.paletteCollapsedHint}>
                                        +{availableColors.length - PALETTE_PREVIEW_LIMIT}
                                    </span>
                                ) : null}
                            </div>
                            <div className={styles.paletteChevronRow}>
                                <button
                                    type="button"
                                    className={styles.paletteChevronBtn}
                                    onClick={() => {
                                        if (paletteLoading) return;
                                        if (paletteOpen) {
                                            onTogglePalette?.();
                                            return;
                                        }
                                        if (!availableColors.length) {
                                            onFetchColors?.();
                                            return;
                                        }
                                        onTogglePalette?.();
                                    }}
                                    disabled={paletteLoading}
                                    aria-label={paletteOpen ? "Ocultar paleta de colores" : "Mostrar paleta de colores"}
                                    title={paletteOpen ? "Ocultar paleta" : "Mostrar paleta"}
                                >
                                    <PaletteChevronIcon expanded={paletteOpen} />
                                </button>
                            </div>

                            {selectedColor ? (
                                <div className={styles.colorInfoRow}>
                                    <span
                                        className={styles.colorInfoChip}
                                        style={{ background: selectedColor.code || "linear-gradient(45deg, #eaeaea, #cdcdcd)" }}
                                    />
                                    <span>{sanitizeColorName(selectedColor.name, "Color elegido")}</span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            )}
        </section>
    );
};

export default MaterialColorView;
